import {
  Component,
  Element,
  getAssetPath,
  h,
  Host,
  Prop,
  State,
  Watch
} from "@stencil/core";

import { ChCheckboxCustomEvent } from "@genexus/chameleon-controls-library";
import { MercuryBundles } from "@genexus/mercury";
import { Locale } from "../../common/locale";
import { FormSubmitResult } from "../../components";
import {
  ControlValidation,
  validateControls
} from "../../common/form-validation";
import { config } from "../../common/config";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "utils/form",
  "utils/spacing",
  "utils/layout",
  "chameleon/scrollbar",
  "components/checkbox",
  "components/button",
  "components/edit",
  "components/tooltip"
];

// ch-checkbox
const CHECKED_VALUE = "on";
const SET_NEW_VERSION_AS_ACTIVE_ID = "set-new-version-as-active";
const CREATE_LINKED_LOCAL_VERSION_ID = "create-linked-local-version";
const CREATE_AS_PROTECTED_VERSION_ID = "create-as-protected-version";

@Component({
  tag: "gx-ide-new-version",
  styleUrl: "new-version.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/new-version"]
})
export class GxIdeNewVersion {
  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;

  #versionNameEl!: HTMLChEditElement;
  #shortcutsSrc = getAssetPath(`./gx-ide-assets/new-version/shortcuts.json`);

  @Element() el: HTMLGxIdeNewVersionElement;

  /**
   * The version selected name
   */
  @State() versionName: string;

  /**
   * The version selected description
   */
  @State() versionDescription: string;

  /**
   * Indicates if the version description has been edited
   */
  @State() versionDescriptionEdited: boolean = false;

  /**
   * Checkbox value to set the new version as active
   */
  @State() setNewVersionAsActive: boolean = false;

  /**
   * Checkbox value to create new remote version linked to the local version
   */
  @State() createLinkedLocalVersion: boolean = false;

  /**
   * Checkbox value to create new remote version as protected version
   */
  @State() createAsProtectedVersion: boolean = false;

  @State() validatableControls = new Map<string, ControlValidation>();

  /**
   * Indicates if the component is going to be used to create a new remote version
   */
  @Prop() readonly isRemoteOperation: boolean = false;

  /**
   * Indicates if the component is going to be used to create a new remote frozen version
   */
  @Prop() readonly isFreezeOperation: boolean = false;

  /**
   * Indicates if the component must show the checkbox to create a linked local version
   */
  @Prop() readonly showLinkedLocalVersionCheckbox: boolean = false;

  /**
   * Callback that must be invoked when the 'Cancel' button is pressed.
   */
  @Prop() readonly cancelCallback: () => Promise<void>;

  /**
   * Callback that must be invoked when the 'Create' button is pressed.
   */
  @Prop() readonly createCallback: (
    newVersionData: NewVersionData
  ) => Promise<FormSubmitResult>;

  /**
   * This property is used to set the initial values of the input fields when the component is loaded.
   */
  @Prop() readonly defaultInputValues: NewVersionData;
  @Watch("defaultInputValues")
  defaultInputValuesChanged(newDefaultInputValues: NewVersionData) {
    this.versionName = newDefaultInputValues?.versionName;
    this.versionDescription = newDefaultInputValues?.versionDescription;
  }

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.defaultInputValuesChanged(this.defaultInputValues);
  }

  componentDidLoad() {
    this.#initializeValidatableControls();
  }

  #handleCheckBoxValueChange = (
    event: ChCheckboxCustomEvent<any> | InputEvent
  ) => {
    const target = event.target as HTMLInputElement;
    if (target.id === SET_NEW_VERSION_AS_ACTIVE_ID) {
      this.setNewVersionAsActive = event.detail === CHECKED_VALUE;
    } else if (target.id === CREATE_LINKED_LOCAL_VERSION_ID) {
      this.createLinkedLocalVersion = event.detail === CHECKED_VALUE;
    } else if (target.id === CREATE_AS_PROTECTED_VERSION_ID) {
      this.createAsProtectedVersion = event.detail === CHECKED_VALUE;
    }
  };

  #handleNameValueChange = (event: CustomEvent<string> | InputEvent) => {
    this.versionName = event.detail as string;
    if (!this.versionDescriptionEdited) {
      this.versionDescription = event.detail as string;
    }
  };

  #handleDescriptionValueChange = (event: CustomEvent<string> | InputEvent) => {
    this.versionDescription = event.detail as string;
    if (this.versionDescription === "") {
      this.versionDescriptionEdited = false;
      this.versionDescription = this.versionName;
    } else {
      this.versionDescriptionEdited = true;
    }
  };

  #cancelClickHandler = async () => {
    this.cancelCallback();
  };

  #createClickHandler = async () => {
    const newVersionData: NewVersionData = {
      versionName: this.versionName,
      versionDescription: this.versionDescription,
      setNewVersionAsActive: this.setNewVersionAsActive,
      createLocalLinkedVersion: this.createLinkedLocalVersion,
      createProtectedVersion: this.createAsProtectedVersion
    };
    const formSubmitResult = await this.createCallback(newVersionData);
    this.validatableControls = validateControls(
      formSubmitResult,
      this.validatableControls
    );
  };

  #initializeValidatableControls = () => {
    // populate validatableControls with the controls that could have errors.
    const validatableControls: HTMLElement[] = [this.#versionNameEl];
    validatableControls.forEach(validatableControl => {
      if (validatableControl.id) {
        this.validatableControls.set(validatableControl.id, {
          reference: validatableControl,
          hasError: false,
          message: undefined
        });
      }
    });
  };

  // TODO: evaluate if this should be a global helper function.
  // It is used on other dialogs as well.
  #evaluateTooltipRender = (
    controlReference: HTMLElement
  ): HTMLChTooltipElement =>
    this.validatableControls?.get(controlReference?.id)?.hasError &&
    this.validatableControls.get(controlReference.id)?.message && (
      <ch-tooltip
        class="tooltip"
        actionElement={controlReference as unknown as HTMLButtonElement}
        blockAlign={config.tooltipSettings.blockAlign}
        inlineAlign={config.tooltipSettings.inlineAlign}
        delay={config.tooltipSettings.delay}
      >
        {this.validatableControls.get(controlReference.id).message}
      </ch-tooltip>
    );

  render() {
    const createButtonDisabled =
      !this.versionName || this.versionName.length === 0;

    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts src={this.#shortcutsSrc}></ch-shortcuts>
        <section class="section">
          <div class="main scrollable field-group spacing-body">
            <div class="field field-block">
              <label class="label" htmlFor="version-name">
                {this.#componentLocale.labels.name}
              </label>
              <ch-edit
                type="text"
                id="version-name"
                class="input"
                placeholder={this.#componentLocale.placeHolders.name}
                value={this.versionName}
                onInput={this.#handleNameValueChange}
                ref={(el: HTMLChEditElement) =>
                  (this.#versionNameEl = el as HTMLChEditElement)
                }
                autoFocus
              ></ch-edit>
              {this.#evaluateTooltipRender(this.#versionNameEl)}
            </div>
            <div class="field field-block">
              <label class="label" htmlFor="version-description">
                {this.#componentLocale.labels.description}
              </label>
              <ch-edit
                type="text"
                id="version-description"
                class="input"
                placeholder={this.#componentLocale.placeHolders.description}
                value={this.versionDescription}
                onInput={this.#handleDescriptionValueChange}
              ></ch-edit>
            </div>
            {!this.isRemoteOperation ? (
              <div class="field field-inline">
                <ch-checkbox
                  class="checkbox"
                  id="set-new-version-as-active"
                  checkedValue={CHECKED_VALUE}
                  onInput={this.#handleCheckBoxValueChange}
                  caption={this.#componentLocale.labels.setActive}
                ></ch-checkbox>
              </div>
            ) : (
              <div class="layout layout--cols-2">
                {this.showLinkedLocalVersionCheckbox && (
                  <div class="layout__panel">
                    <ch-checkbox
                      class="checkbox"
                      id="create-linked-local-version"
                      checkedValue={CHECKED_VALUE}
                      onInput={this.#handleCheckBoxValueChange}
                      caption={
                        this.#componentLocale.labels.createLinkedLocalVersion
                      }
                    ></ch-checkbox>
                  </div>
                )}
                {!this.isFreezeOperation && (
                  <div class="layout__panel">
                    <ch-checkbox
                      class="checkbox"
                      id="create-as-protected-version"
                      checkedValue={CHECKED_VALUE}
                      onInput={this.#handleCheckBoxValueChange}
                      caption={
                        this.#componentLocale.labels.createAsProtectedVersion
                      }
                    ></ch-checkbox>
                  </div>
                )}
              </div>
            )}
          </div>

          <footer class="dialog-footer-with-border spacing-body">
            <div class="buttons-spacer">
              <button
                id="cancel-button"
                class="button-secondary"
                onClick={this.#cancelClickHandler}
              >
                {this.#componentLocale.buttons.cancel}
              </button>

              <button
                id="create-button"
                class="button-primary"
                onClick={this.#createClickHandler}
                disabled={createButtonDisabled}
              >
                {this.#componentLocale.buttons.create}
              </button>
            </div>
          </footer>
        </section>
      </Host>
    );
  }
}

export type NewVersionData = {
  versionName: string;
  versionDescription: string;
  setNewVersionAsActive?: boolean;
  createLocalLinkedVersion?: boolean;
  createProtectedVersion?: boolean;
};
